<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import contactCrmAPI from 'dashboard/api/contactCrm';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// Import tab components
import CrmTabSummary from './crm-tabs/CrmTabSummary.vue';
import CrmTabData from './crm-tabs/CrmTabData.vue';
import CrmTabAddresses from './crm-tabs/CrmTabAddresses.vue';
import CrmTabTimeline from './crm-tabs/CrmTabTimeline.vue';
import CrmTabDocuments from './crm-tabs/CrmTabDocuments.vue';
import CrmTabProducts from './crm-tabs/CrmTabProducts.vue';
import CrmTabAppointments from './crm-tabs/CrmTabAppointments.vue';
import CrmTabNotes from './crm-tabs/CrmTabNotes.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['close', 'contact-updated']);

const { t } = useI18n();

// State
const isLoading = ref(true);
const isSaving = ref(false);
const activeTab = ref('summary');
const crmData = ref(null);
const hasUnsavedChanges = ref(false);

// Tabs configuration
const tabs = [
  { id: 'summary', label: 'Resumo', icon: 'home' },
  { id: 'data', label: 'Dados', icon: 'person' },
  { id: 'addresses', label: 'Endereços', icon: 'location' },
  { id: 'products', label: 'Produtos', icon: 'box' },
  { id: 'appointments', label: 'Agendamentos', icon: 'calendar' },
  { id: 'notes', label: 'Notas', icon: 'note' },
  { id: 'timeline', label: 'Timeline', icon: 'timeline' },
  { id: 'documents', label: 'Documentos', icon: 'document' },
];

// Computed
const contact = computed(() => crmData.value?.contact || {});
const contactName = computed(
  () => contact.value?.full_name || contact.value?.name || 'Contato'
);
const contactAvatar = computed(() => contact.value?.avatar || null);

// Methods
const loadCrmData = async () => {
  isLoading.value = true;
  try {
    const response = await contactCrmAPI.getCrmData(props.contactId);
    crmData.value = response.data;
  } catch (error) {
    useAlert(t('CRM.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const handleSave = async updatedData => {
  isSaving.value = true;
  try {
    const response = await contactCrmAPI.updateCrmData(
      props.contactId,
      updatedData
    );
    crmData.value.contact = response.data.contact;
    hasUnsavedChanges.value = false;
    useAlert(t('CRM.SUCCESS.UPDATED'));
    emit('contact-updated', response.data.contact);
  } catch (error) {
    useAlert(t('CRM.ERRORS.UPDATE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  if (hasUnsavedChanges.value) {
    if (
      window.confirm('Você tem alterações não salvas. Deseja sair mesmo assim?')
    ) {
      emit('close');
    }
  } else {
    emit('close');
  }
};

const handleKeyDown = event => {
  if (event.key === 'Escape' && !event.shiftKey && !event.ctrlKey) {
    handleClose();
  }
};

const refreshData = async () => {
  await loadCrmData();
};

// Lifecycle
onMounted(() => {
  loadCrmData();
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});

// Watch for contactId changes
watch(
  () => props.contactId,
  () => {
    loadCrmData();
  }
);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    @click.self="handleClose"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col overflow-hidden"
    >
      <!-- HEADER -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-500 to-woot-500"
      >
        <div class="flex items-center gap-4">
          <Avatar
            :src="contactAvatar"
            :name="contactName"
            :size="48"
            rounded-full
            class="ring-2 ring-white/50"
          />
          <div>
            <h2 class="text-xl font-bold text-white">
              CRM - {{ contactName }}
            </h2>
            <p class="text-sm text-white/80">
              Gerenciamento completo do contato
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <!-- Save indicator -->
          <span
            v-if="hasUnsavedChanges"
            class="text-xs text-yellow-300 flex items-center gap-1"
          >
            <fluent-icon icon="warning" size="14" />
            Alterações não salvas
          </span>

          <!-- Close button -->
          <button
            class="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            @click="handleClose"
          >
            <fluent-icon icon="dismiss" size="24" />
          </button>
        </div>
      </div>

      <!-- TABS -->
      <div
        class="flex items-center gap-1 px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
          :class="
            activeTab === tab.id
              ? 'bg-woot-500 text-white shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          "
          @click="activeTab = tab.id"
        >
          <fluent-icon :icon="tab.icon" size="16" />
          {{ tab.label }}
        </button>
      </div>

      <!-- CONTENT -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Loading state -->
        <div v-if="isLoading" class="flex items-center justify-center h-full">
          <div class="text-center">
            <div
              class="w-12 h-12 border-4 border-woot-500 border-t-transparent rounded-full animate-spin mx-auto"
            />
            <p class="mt-4 text-slate-500 dark:text-slate-400">
              Carregando dados do CRM...
            </p>
          </div>
        </div>

        <!-- Tab content -->
        <template v-else>
          <CrmTabSummary
            v-if="activeTab === 'summary'"
            :crm-data="crmData"
            :contact-id="contactId"
            @refresh="refreshData"
          />

          <CrmTabData
            v-else-if="activeTab === 'data'"
            :contact="contact"
            :is-saving="isSaving"
            @save="handleSave"
            @change="hasUnsavedChanges = true"
          />

          <CrmTabAddresses
            v-else-if="activeTab === 'addresses'"
            :contact-id="contactId"
            :addresses="crmData?.addresses || []"
            @refresh="refreshData"
          />

          <CrmTabProducts
            v-else-if="activeTab === 'products'"
            :contact-id="contactId"
            :conversation-id="conversationId"
            :products="crmData?.products || []"
            @refresh="refreshData"
          />

          <CrmTabAppointments
            v-else-if="activeTab === 'appointments'"
            :contact-id="contactId"
            :conversation-id="conversationId"
            :appointments="crmData?.appointments || []"
            @refresh="refreshData"
          />

          <CrmTabNotes
            v-else-if="activeTab === 'notes'"
            :contact-id="contactId"
            :conversation-id="conversationId"
            :notes="crmData?.notes || {}"
            @refresh="refreshData"
          />

          <CrmTabTimeline
            v-else-if="activeTab === 'timeline'"
            :contact-id="contactId"
            :timeline="crmData?.timeline || []"
            @refresh="refreshData"
          />

          <CrmTabDocuments
            v-else-if="activeTab === 'documents'"
            :contact-id="contactId"
            :documents="crmData?.documents || []"
            @refresh="refreshData"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for tabs */
.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}
</style>
